import React, { useContext, useState, useEffect, useMemo } from "react";
import {
  Box,
  Container,
  Grid,
  LinearProgress,
  Badge,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Link,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import bkstApi from "../../common/api";
import { Link as RouterLink } from "react-router-dom";
import ConfigContext from "../../common/context/ConfigContext";
import { AppStateContext } from "../../common/context/AppStateContext";
import ProductSummary from "../component/ProductSummary";
import {
  anyWordStartsWith,
  checkItemAvailabilityByDate,
  checkStoreAvailabilityByDate,
  getItemUrl,
} from "../../common/util";
import FilterListIcon from "@mui/icons-material/FilterList";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import SearchIcon from "@mui/icons-material/Search";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CustomItemSummaryTile from "../component/CustomItemSummaryTile";
import moment from "moment";
import FulfillmentOptionsPreCheck from "../../cart/component/FulfillmentOptionsPreCheck";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";

export default function StandardCatalog() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const siteConfig = useContext(ConfigContext);
  const { cart, resetCartState } = useContext(AppStateContext);

  const [categoryMap, setCategoryMap] = useState("");

  const [query, setQuery] = useState("");
  const [filtered, setFiltered] = useState([]);
  const [date, setDate] = useState(() => {
    const d = sessionStorage.getItem("cart.date");
    return d || "";
  });

  const [category, setCategory] = useState("");
  const [products, setProducts] = useState([]);
  const [storeLocation, setStoreLocation] = useState("");

  const [loading, setLoading] = useState(true);
  const [showUnavailableList, setShowUnavailableList] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const categoryNavRef = React.useRef(null);
  const categoryLinksRef = React.useRef([]);
  const [focusedCategoryIndex, setFocusedCategoryIndex] = useState(0);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const handleDateChange = (newDate) => {
    setDate(newDate);
    if (newDate) {
      sessionStorage.setItem("cart.date", newDate);
    } else {
      sessionStorage.removeItem("cart.date");
    }
    resetCartState();
  };

  useEffect(() => {
    bkstApi(`/site/${siteConfig.id}/product/collection/shop`)
      .then((res) => {
        let sortedByRank = res.data.products.sort((o1, o2) => {
          let rslt = (+o2.rank || 0) - (+o1.rank || 0);
          if (rslt === 0) {
            rslt = o1.title > o2.title ? 1 : -1;
          }
          return rslt;
        });
        setProducts(sortedByRank);

        let map = {};
        for (const p of sortedByRank) {
          const cats = p.category || [p.type.toLowerCase()];
          for (let cat of cats) {
            cat = cat.toLowerCase();
            // exclude these categories
            if (
              !(
                cat.includes("halloween") ||
                cat.includes("thanksgiving") ||
                cat.includes("christmas") ||
                cat.includes("mardi") ||
                cat.includes("valentine") ||
                cat.includes("patrick") ||
                cat.includes("easter") ||
                cat.includes("mother") ||
                cat.includes("father")
              )
            ) {
              const arr = map[cat] || [];
              arr.push(p);
              map[cat] = arr;
            }
          }
        }
        setCategoryMap(map);
        setStoreLocation(res.data.storeLocation);

        setLoading(false);

        setFiltered(sortedByRank);

        if (window.location.hash) {
          const hashCategory = window.location.hash.substring(1).replace(/-/g, " ");
          console.log("window.location.hash", window.location.hash, hashCategory);

          setCategory(hashCategory);
          // Browser will handle scrolling via hash, no need to manually scroll
        }
      })
      .catch((error) => {
        console.error("Error:", error);
      });
  }, []);

  const handleQueryChange = (q) => {
    setQuery(q);
    setCategory("");

    // Clear hash when searching (only if there was a hash)
    if (window.location.hash) {
      window.history.replaceState(null, "", window.location.pathname + window.location.search);
    }

    // Filter products immediately without delay
    setFiltered(
      products.filter(
        (o) =>
          anyWordStartsWith(o.title, q) ||
          anyWordStartsWith(o.category ? o.category.join(", ") : o.type, q)
      )
    );

    // Scroll to product list to show search results
    if (q) {
      // Use setTimeout to ensure DOM has updated with filtered results
      setTimeout(() => {
        const productListElement = document.getElementById("product-list");
        if (productListElement) {
          productListElement.scrollIntoView({ behavior: "smooth", block: "start" });
        } else {
          // Fallback to top if element not found
          window.scrollTo({ top: 0, behavior: "smooth" });
        }
      }, 0);
    }
  };

  const getRank = (cat, arr) => {
    cat = cat.toLowerCase();
    if (cat.includes("featured")) return 10000;
    if (cat.includes("hashanah")) return 8000;
    if (cat.includes("kippur")) return 7000;

    const rank = arr.reduce((acc, o) => +acc + +o.rank, 0);
    const itemsWithEndDate = arr.filter((o) => o.rules?.date?.max);

    if (itemsWithEndDate.length > 0 && itemsWithEndDate.length / arr.length > 0.4) {
      // if half of items in category have end date, sort by end date
      return 8000;
    } else if (rank > arr.length) {
      // sort by bestsellers
      return rank;
    } else {
      // sort by number of items in category
      return arr.length; // all other strings
    }
  };

  const sortCategories = ([k1, v1], [k2, v2]) => {
    const rankA = getRank(k1, v1);
    const rankB = getRank(k2, v2);

    if (rankA === rankB) {
      return k1.localeCompare(k2);
    } else {
      return rankB - rankA;
    }
  };

  const isItemAvailable = (product) => {
    if (!date) return true;
    return (
      checkStoreAvailabilityByDate(siteConfig.id, siteConfig.storeLocations[0], date) &&
      checkItemAvailabilityByDate(siteConfig.id, [product], date, siteConfig.storeLocations[0])
    );
  };

  const separateByAvailability = (products) => {
    if (!date) return { available: products, unavailable: [] };
    const available = products.filter((p) => isItemAvailable(p));
    const unavailable = products.filter((p) => !isItemAvailable(p));
    return { available, unavailable };
  };

  const aggregatedUnavailable = useMemo(() => {
    if (!date) return [];
    const source = filtered.length < products.length ? filtered : products;
    return source.filter((product) => !isItemAvailable(product));
  }, [date, filtered, products]);

  useEffect(() => {
    setShowUnavailableList(false);
  }, [date, query]);

  useEffect(() => {
    if (query) {
      setShowSearch(true);
    }
  }, [query]);

  /** Scroll to hash fragment after content is rendered */
  useEffect(() => {
    if (!loading && filtered.length === products.length && window.location.hash) {
      // Wait for DOM to update, then scroll to the hash element
      // Use requestAnimationFrame to ensure DOM is fully rendered
      requestAnimationFrame(() => {
        setTimeout(() => {
          const hash = window.location.hash.substring(1);
          const element = document.getElementById(hash);
          if (element) {
            element.scrollIntoView({ behavior: "smooth", block: "start" });
          }
        }, 100);
      });
    }
  }, [loading, filtered, products.length]);

  const checkScrollButtons = () => {
    if (categoryNavRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = categoryNavRef.current;
      // Left button should be enabled if we can scroll left (scrollLeft > 0)
      // Use a small threshold to account for sub-pixel scrolling
      const canScrollLeft = scrollLeft > 1;
      // Right button should be enabled if we can scroll right (not at the end)
      // Use a small threshold to account for rounding
      const canScrollRight = scrollLeft < scrollWidth - clientWidth - 1;

      setCanScrollLeft(canScrollLeft);
      setCanScrollRight(canScrollRight);
    }
  };

  useEffect(() => {
    // Initial check
    const timer = setTimeout(() => {
      checkScrollButtons();
    }, 100);

    const navElement = categoryNavRef.current;
    if (navElement) {
      navElement.addEventListener("scroll", checkScrollButtons, { passive: true });
      // Check on resize
      window.addEventListener("resize", checkScrollButtons);
      return () => {
        clearTimeout(timer);
        navElement.removeEventListener("scroll", checkScrollButtons);
        window.removeEventListener("resize", checkScrollButtons);
      };
    }
    return () => clearTimeout(timer);
  }, [categoryMap, showSearch]);

  const scrollCategories = (direction) => {
    if (categoryNavRef.current) {
      const scrollAmount = 200;
      const currentScroll = categoryNavRef.current.scrollLeft;
      categoryNavRef.current.scrollTo({
        left: currentScroll + (direction === "left" ? -scrollAmount : scrollAmount),
        behavior: "smooth",
      });
      // Check buttons after scroll animation completes
      setTimeout(() => {
        checkScrollButtons();
      }, 300);
    }
  };

  if (loading) {
    return <LinearProgress aria-label="Loading products" />;
  } else {
    return (
      <Container maxWidth="lg" disableGutters={isMobile} sx={{ paddingTop: "2rem" }}>
        <Box
          sx={{
            backgroundColor: "#F7F7F7",
            position: "-webkit-sticky",
            position: "sticky",
            top: "0",
            marginBottom: "2rem",
            zIndex: 10,
            "&::after": {
              content: '""',
              position: "absolute",
              bottom: "-20px",
              left: 0,
              right: 0,
              height: "20px",
              background:
                "linear-gradient(to bottom, rgba(247, 247, 247, 1), rgba(247, 247, 247, 0))",
              pointerEvents: "none",
              zIndex: 1,
            },
          }}
        >
          <Box px={1} pt={1}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={10}>
                <Box
                  mb={1}
                  sx={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}
                >
                  <Typography variant="h1" sx={{ whiteSpace: "nowrap" }}>
                    Online Shop
                  </Typography>
                  <FulfillmentOptionsPreCheck
                    date={date}
                    onChange={(o) => {
                      handleDateChange(o.date);
                    }}
                  >
                    <Link
                      component="span"
                      sx={{ cursor: "pointer", whiteSpace: "nowrap" }}
                      tabIndex={-1}
                    >
                      {date ? moment(date).format("ddd, MMM D, YYYY") : "Pick a Date"}
                    </Link>
                  </FulfillmentOptionsPreCheck>
                </Box>
              </Grid>
              <Grid item xs={2} textAlign="right">
                <Box sx={{ flexGrow: 0, minWidth: "2rem" }}>
                  <IconButton
                    component={RouterLink}
                    to={cart.quantity > 0 ? `/cart/${cart.id}` : `/shop`}
                    aria-label="view cart"
                  >
                    <Badge badgeContent={cart.quantity} color="primary">
                      <ShoppingCartIcon />
                    </Badge>
                  </IconButton>
                </Box>
              </Grid>
            </Grid>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              borderTop: "2px solid #DDD",
              borderBottom: "2px solid #DDD",
              overflow: "hidden",
            }}
          >
            {showSearch ? (
              <TextField
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus={showSearch}
                id="query"
                value={query}
                label=""
                name="query"
                variant="outlined"
                onChange={(e) => {
                  handleQueryChange(e.target.value);
                }}
                placeholder="Filter items by name or category"
                aria-label="Filter items by name or category"
                InputProps={{
                  startAdornment: (
                    <InputAdornment sx={{ paddingRight: "10px" }}>
                      <FilterListIcon />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment>
                      <IconButton
                        size="small"
                        onClick={() => {
                          handleQueryChange("");
                          setShowSearch(false);
                        }}
                        aria-label="clear search"
                      >
                        <CloseOutlinedIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  flex: 1,
                }}
              />
            ) : (
              <IconButton onClick={() => setShowSearch(true)} aria-label="search items">
                <SearchIcon sx={{ color: "#333" }} />
              </IconButton>
            )}
            {!showSearch && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  flex: 1,
                  position: "relative",
                  overflow: "hidden",
                  gap: 0.5,
                }}
              >
                <IconButton
                  onClick={() => scrollCategories("left")}
                  disabled={!canScrollLeft}
                  sx={{
                    flexShrink: 0,
                    color: "#333",
                    "&.Mui-disabled": {
                      opacity: 0.3,
                    },
                  }}
                  aria-label="Scroll categories left"
                >
                  <ChevronLeftIcon />
                </IconButton>
                <Box
                  ref={categoryNavRef}
                  component="nav"
                  role="tablist"
                  aria-label="Product categories"
                  onKeyDown={(e) => {
                    const visibleCategories = Object.entries(categoryMap)
                      .sort(sortCategories)
                      .filter(([cat, arr]) => separateByAvailability(arr).available.length > 0);
                    const maxIndex = visibleCategories.length - 1;

                    if (e.key === "ArrowRight" || e.key === "ArrowDown") {
                      e.preventDefault();
                      const newIndex =
                        focusedCategoryIndex < maxIndex ? focusedCategoryIndex + 1 : 0;
                      setFocusedCategoryIndex(newIndex);
                      categoryLinksRef.current[newIndex]?.focus();
                    } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
                      e.preventDefault();
                      const newIndex =
                        focusedCategoryIndex > 0 ? focusedCategoryIndex - 1 : maxIndex;
                      setFocusedCategoryIndex(newIndex);
                      categoryLinksRef.current[newIndex]?.focus();
                    } else if (e.key === "Home") {
                      e.preventDefault();
                      setFocusedCategoryIndex(0);
                      categoryLinksRef.current[0]?.focus();
                    } else if (e.key === "End") {
                      e.preventDefault();
                      setFocusedCategoryIndex(maxIndex);
                      categoryLinksRef.current[maxIndex]?.focus();
                    }
                  }}
                  sx={{
                    flex: 1,
                    display: "flex",
                    overflowX: "auto",
                    overflowY: "hidden",
                    scrollbarWidth: "none",
                    msOverflowStyle: "none",
                    "&::-webkit-scrollbar": {
                      display: "none",
                    },
                    whiteSpace: "nowrap",
                    minWidth: 0,
                  }}
                >
                  {Object.entries(categoryMap)
                    .sort(sortCategories)
                    .filter(([cat, arr]) => separateByAvailability(arr).available.length > 0)
                    .map(([cat, arr], idx) => {
                      const isSelected = category === cat;
                      return (
                        <Link
                          key={cat}
                          ref={(el) => (categoryLinksRef.current[idx] = el)}
                          href={`#${cat.replace(/\s+/g, "-")}`}
                          role="tab"
                          aria-selected={isSelected}
                          tabIndex={idx === focusedCategoryIndex ? 0 : -1}
                          onFocus={() => setFocusedCategoryIndex(idx)}
                          onClick={(e) => {
                            // Update state first
                            setCategory(cat);
                            setFocusedCategoryIndex(idx);
                            if (query) {
                              setQuery("");
                              setFiltered(products);
                              setShowSearch(false);
                            }
                            // Let browser handle scrolling and URL hash via href
                            // The href will automatically scroll to the element with that id
                          }}
                          sx={{
                            minWidth: "50px",
                            padding: "12px",
                            color: "#333",
                            textDecoration: "none",
                            fontSize: "0.75rem",
                            fontWeight: isSelected ? 600 : 400,
                            borderBottom: isSelected
                              ? `2px solid ${theme.palette.primary.main}`
                              : "2px solid transparent",
                            borderRadius: 0,
                            display: "inline-flex",
                            alignItems: "center",
                            whiteSpace: "nowrap",
                            flexShrink: 0,
                            "&:focus-visible": {
                              outline: "none",
                              boxShadow: `inset 0 0 0 2px ${theme.palette.primary.main}`,
                              zIndex: 1,
                            },
                            "&:hover": {
                              backgroundColor: "rgba(0, 0, 0, 0.04)",
                            },
                            textTransform: "uppercase",
                          }}
                        >
                          {cat}
                        </Link>
                      );
                    })}
                </Box>
                <IconButton
                  onClick={() => scrollCategories("right")}
                  disabled={!canScrollRight}
                  sx={{
                    flexShrink: 0,
                    color: "#333",
                    "&.Mui-disabled": {
                      opacity: 0.3,
                    },
                  }}
                  aria-label="Scroll categories right"
                >
                  <ChevronRightIcon />
                </IconButton>
              </Box>
            )}
          </Box>
        </Box>

        <Box id="product-list" sx={{ scrollMarginTop: "150px", paddingBottom: "200px" }}>
          {filtered.length < products.length
            ? (() => {
                const { available } = separateByAvailability(filtered);
                return (
                  <Box>
                    {available.length > 0 ? (
                      <Box mb={3}>
                        <Typography variant="h4" component="h2">
                          Search results for "{query}" ({available.length})
                        </Typography>
                      </Box>
                    ) : (
                      <Box mb={3}>
                        <Typography variant="h4" component="h2" gutterBottom>
                          No available items match "{query}"
                        </Typography>
                        <Typography variant="body1" color="textSecondary">
                          {query
                            ? `Adjust or clear your search criteria to view matching items.`
                            : "Please try a different search criteria."}
                        </Typography>
                      </Box>
                    )}
                    {available.length > 0 && (
                      <Grid container spacing={2}>
                        {available.map((o, index) => {
                          return (
                            <Grid item xs={12} sm={6} key={`filtered-${index}`}>
                              <FulfillmentOptionsPreCheck
                                product={o}
                                date={date}
                                onChange={(obj) => {
                                  handleDateChange(obj.date);
                                }}
                                target={getItemUrl(o)}
                              >
                                <ProductSummary
                                  key={`rslt-${index}`}
                                  product={o}
                                  query={query}
                                  isAvailable={true}
                                  tabIndex={-1}
                                />
                              </FulfillmentOptionsPreCheck>
                            </Grid>
                          );
                        })}
                      </Grid>
                    )}
                  </Box>
                );
              })()
            : Object.entries(categoryMap)
                .sort(sortCategories)
                .map(([key]) => {
                  const { available } = separateByAvailability(categoryMap[key]);
                  if (available.length === 0) {
                    return null;
                  }
                  return (
                    <Box
                      mb={6}
                      id={key.replace(/\s+/g, "-")}
                      role="region"
                      aria-labelledby={`category-heading-${key.replace(/\s+/g, "-")}`}
                      sx={{ scrollMarginTop: "120px" }}
                    >
                      <Box
                        pl={1}
                        mb={2}
                        textAlign={"left"}
                        sx={{ borderLeft: "3px solid", borderColor: "text.secondary" }}
                      >
                        <Typography
                          id={`category-heading-${key.replace(/\s+/g, "-")}`}
                          variant="h5"
                          component="h2"
                        >
                          {key.toUpperCase()} ({available.length})
                        </Typography>
                      </Box>
                      <Grid container spacing={3}>
                        {available.map((product, index) => {
                          return (
                            <Grid item xs={12} sm={6}>
                              <FulfillmentOptionsPreCheck
                                product={product}
                                date={date}
                                onChange={(obj) => {
                                  handleDateChange(obj.date);
                                }}
                                target={getItemUrl(product)}
                              >
                                <ProductSummary
                                  key={`rslt-${index}`}
                                  product={product}
                                  query={query}
                                  isAvailable={true}
                                  tabIndex={-1}
                                />
                              </FulfillmentOptionsPreCheck>
                            </Grid>
                          );
                        })}
                        {!(
                          siteConfig.id === "Z3bGmikh3cWqw3HYKGIA" ||
                          siteConfig.id === "ZOsiHlROlfEcAO7PGGCK"
                        ) &&
                          (key.toLowerCase() === "cake" ||
                            key.toLowerCase() === "cakes" ||
                            key.toLowerCase().includes(" cake")) && (
                            <CustomItemSummaryTile></CustomItemSummaryTile>
                          )}
                      </Grid>
                    </Box>
                  );
                })}
          {date && aggregatedUnavailable.length > 0 && (
            <Box id="unavailable-items" sx={{ marginTop: 6 }}>
              <Box mb={2} textAlign="left">
                <Typography gutterBottom variant="h5" component="h2">
                  UNAVAILABLE ({aggregatedUnavailable.length})
                </Typography>
                <Typography variant="body1" color="textSecondary">
                  <Link
                    sx={{ cursor: "pointer" }}
                    onClick={() => setShowUnavailableList((prev) => !prev)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setShowUnavailableList((prev) => !prev);
                      }
                    }}
                    role="button"
                    tabIndex={0}
                    aria-label={`${showUnavailableList ? "Hide" : "Show"} unavailable items for ${moment(date).format("ddd, MMM D, YYYY")}`}
                  >
                    Click here
                  </Link>{" "}
                  to view items unavailable for {moment(date).format("ddd, MMM D, YYYY")}.
                </Typography>
              </Box>

              {showUnavailableList && (
                <Grid container spacing={3} sx={{ marginTop: 1 }}>
                  {aggregatedUnavailable.map((product, index) => (
                    <Grid item xs={12} sm={6} key={`unavailable-${index}`}>
                      <ProductSummary
                        product={product}
                        query={query}
                        date={date}
                        setDate={setDate}
                        isAvailable={false}
                      />
                    </Grid>
                  ))}
                </Grid>
              )}
            </Box>
          )}
        </Box>
      </Container>
    );
  }
}
